import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import MenuIcon from "@mui/icons-material/Menu";
import Container from "@mui/material/Container";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import MenuItem from "@mui/material/MenuItem";
import Icon from "@mui/material/Icon";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LogoutIcon from "@mui/icons-material/Logout";

import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Logo from "./Logo";

const pages = [
  { name: "Acerca de Nosotros", link: "/AboutUs", icon: "home" },
  { name: "Evidencias & Resultados", link: "/EvidenceResults", icon: "eco" },
  { name: "Noticias & Eventos", link: "/NewsEvents", icon: "event" },
  { name: "Certificate", link: "/login", icon: "workspace_premium" },
];

function ResponsiveAppBar() {
  const [anchorElNav, setAnchorElNav] = React.useState(null);
  const [anchorElUser, setAnchorElUser] = React.useState(null);
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const getPageLink = (page) => {
    if (page.name === "Certificate") {
      return isAuthenticated ? "/AvailableCourses" : "/login";
    }
    return page.link;
  };

  const handleRedirect = (path) => {
    setAnchorElNav(null);
    navigate(path);
  };

  const handleUserMenuAction = (action) => {
    setAnchorElUser(null);
    if (action === "login") {
      navigate("/login");
    } else if (action === "profile") {
      navigate("/Profile");
    } else if (action === "logout") {
      logout();
    }
  };

  const userInitial = React.useMemo(() => {
    if (!user) return "?";
    const name = user.nombre || user.Nombre || user.nombres;
    const email = user.correo || user.Correo || user.email || "";
    if (name && typeof name === "string") return name.trim().charAt(0).toUpperCase();
    if (email) return email.trim().charAt(0).toUpperCase();
    return "?";
  }, [user]);

  return (
    <AppBar
      position="sticky"
      sx={{
        backgroundColor: "tertiary.main",
        boxShadow: "none",
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
      }}
    >
      <Container maxWidth="xl">
        <Toolbar
          disableGutters
          sx={{
            minHeight: { xs: 72, md: 90 },
            px: { xs: 1, sm: 1.5, md: 0 },
          }}
        >
          <Logo display={{ xs: "none", md: "flex" }}/>
          <Box sx={{ flexGrow: 1, display: { xs: "flex", md: "none" } }}>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{ display: { xs: "block", md: "none" } }}
            >
              {pages.map((page) => (
                <MenuItem
                  key={page.name}
                  onClick={() => handleRedirect(getPageLink(page))}
                >
                  <Typography sx={{ textAlign: "center", fontSize: "0.95rem" }}>
                    {page.name}
                  </Typography>
                </MenuItem>
              ))}
            </Menu>
          </Box>
          <Logo display={{ xs: "flex", md: "none" }} />
          <Box
            sx={{
              flexGrow: 1,
              display: { xs: "none", md: "flex" },
              flexDirection: "row",
              justifyContent: "flex-end",
            }}
          >
            {pages.map((page) => (
              <Button
                key={page.name}
                onClick={() => handleRedirect(getPageLink(page))}
                sx={{
                  m: 0.5,
                  px: 1.25,
                  minHeight: { md: 70, lg: 78 },
                  color: page.name === "Certificate" ? "text.primary" : "white",
                  display: "flex",
                  gap: 0.75,
                  fontSize: { md: "0.8rem", lg: "0.95rem" },
                  fontWeight: 'bold',
                  borderRadius: 1.5,
                  whiteSpace: "nowrap",
                  backgroundColor:
                    page.name === "Certificate"
                      ? "warning.main"
                      : "transparent",
                  "&:hover": {
                    backgroundColor:
                      page.name === "Certificate"
                        ? "warning.dark"
                        : "rgba(255,255,255,0.12)",
                  },
                }}
              >
                <Icon sx={{ fontSize: { md: 17, lg: 19 } }}>{page.icon}</Icon>
                {page.name}
              </Button>
            ))}
          </Box>
          <Box sx={{ flexGrow: 0 }}>
            <Tooltip title={isAuthenticated ? "Cuenta" : "Iniciar sesión"}>
              <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }} aria-label={isAuthenticated ? "Menú de cuenta" : "Iniciar sesión"}>
                {isAuthenticated ? (
                  <Avatar sx={{ bgcolor: "primary.main" }} alt={user?.nombre || "Usuario"}>
                    {userInitial}
                  </Avatar>
                ) : (
                  <Avatar sx={{ bgcolor: "rgba(255,255,255,0.3)" }}>
                    <PersonOutlineIcon />
                  </Avatar>
                )}
              </IconButton>
            </Tooltip>
            <Menu
              sx={{ mt: "45px" }}
              id="menu-appbar-user"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              {isAuthenticated ? (
                <>
                  <MenuItem onClick={() => handleUserMenuAction("profile")}>
                    <PersonOutlineIcon sx={{ mr: 1.5, fontSize: 20 }} />
                    <Typography>Perfil</Typography>
                  </MenuItem>
                  <MenuItem onClick={() => handleUserMenuAction("logout")}>
                    <LogoutIcon sx={{ mr: 1.5, fontSize: 20 }} />
                    <Typography>Cerrar sesión</Typography>
                  </MenuItem>
                </>
              ) : (
                <MenuItem onClick={() => handleUserMenuAction("login")}>
                  <PersonOutlineIcon sx={{ mr: 1.5, fontSize: 20 }} />
                  <Typography>Iniciar sesión</Typography>
                </MenuItem>
              )}
            </Menu>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}
export default ResponsiveAppBar;
